import Segment from './segment';
import Point from './point';
import Line from './line';
import CurveLocation from './curve_location';
import { CURVETIME_EPSILON, EPSILON, GEOMETRIC_EPSILON } from './constant';
import { integrate, findRoot, solveCubic } from './numerical';
export default class Curve {
    constructor(path, segment1 = new Segment(), segment2 = new Segment()) {
        this.path = path;
        this.segment1 = segment1;
        this.segment2 = segment2;
    }
    clone() {
        return new Curve(this.path, this.segment1.clone(), this.segment2.clone());
    }
    getValues() {
        const p1 = this.segment1.point, h1 = this.segment1.handleOut, h2 = this.segment2.handleIn, p2 = this.segment2.point;
        const x1 = p1.x, y1 = p1.y, x2 = p2.x, y2 = p2.y;
        return [
            x1, y1,
            x1 + h1.x, y1 + h1.y,
            x2 + h2.x, y2 + h2.y,
            x2, y2
        ];
    }
    getNearestLocation(point) {
        const values = this.getValues();
        const t = this.getNearestTime(values, point);
        const pt = this.getPoint(values, t);
        return new CurveLocation(this, t, pt, pt && point.getDistance(pt));
    }
    getNearestTime(values, point) {
        const count = 100;
        let minDist = Infinity;
        let minT = 0;
        const refine = (t) => {
            const _point = this.getPoint(values, t);
            if (t >= 0 && t <= 1 && _point) {
                const dist = point.getDistance(_point);
                if (dist && dist < minDist) {
                    minDist = dist;
                    minT = t;
                    return true;
                }
            }
            return false;
        };
        for (let i = 0; i <= count; i++)
            refine(i / count);
        // Now iteratively refine solution until we reach desired precision.
        let step = 1 / (count * 2);
        while (step > CURVETIME_EPSILON) {
            if (!refine(minT - step) && !refine(minT + step))
                step /= 2;
        }
        return minT;
    }
    getNext() {
        const curves = this.path.curves;
        if (curves) {
            if (this.segment1.index + 1 < curves.length)
                return curves[this.segment1.index + 1];
            else
                return this.path.closed ? curves[0] : undefined;
        }
        return undefined;
    }
    getPointAtTime(t) {
        return this.getPoint(this.getValues(), t);
    }
    getPoint(v, t) {
        if (t == undefined || t < 0 || t > 1)
            return undefined;
        let x0 = v[0], y0 = v[1], x1 = v[2], y1 = v[3], x2 = v[4], y2 = v[5], x3 = v[6], y3 = v[7];
        function isZero(value) {
            return value >= -EPSILON && value <= EPSILON;
        }
        // If the curve handles are almost zero, reset the control points to the anchors.
        if (isZero(x1 - x0) && isZero(y1 - y0)) {
            x1 = x0;
            y1 = y0;
        }
        if (isZero(x2 - x3) && isZero(y2 - y3)) {
            x2 = x3;
            y2 = y3;
        }
        const cx = 3 * (x1 - x0), bx = 3 * (x2 - x1) - cx, ax = x3 - x0 - cx - bx, cy = 3 * (y1 - y0), by = 3 * (y2 - y1) - cy, ay = y3 - y0 - cy - by;
        const x = t == 0 ? x0 : t == 1 ? x3 : ((ax * t + bx) * t + cx) * t + x0;
        const y = t == 0 ? y0 : t == 1 ? y3 : ((ay * t + by) * t + cy) * t + y0;
        return new Point(x, y);
    }
    getLocationOf(point) {
        return this.getLocationAtTime(this.getTimeOf(point));
    }
    getLocationAtTime(t) {
        return t && t >= 0 && t <= 1 ? new CurveLocation(this, t) : undefined;
    }
    getTimeOf(point) {
        const values = this.getValues();
        const p0 = new Point(values[0], values[1]);
        const p3 = new Point(values[6], values[7]);
        const t = point.isClose(p0, EPSILON) ? 0
            : point.isClose(p3, EPSILON) ? 1
                : undefined;
        if (t)
            return t;
        // Since we're comparing with geometric epsilon for any other t along
        // the curve, do so as well now for the beginning and end of the curve.
        const coords = [point.x, point.y];
        let roots = [];
        for (let c = 0; c < 2; c++) {
            const count = this.solveCubic(values, c, coords[c], roots, 0, 1);
            for (let i = 0; i < count; i++) {
                const u = roots[i];
                if (point.isClose(this.getPoint(values, u), GEOMETRIC_EPSILON))
                    return u;
            }
        }
        return;
    }
    getTimeAt(offset) {
        const values = this.getValues();
        let start = offset < 0 ? 1 : 0;
        if (offset == 0)
            return start;
        const forward = offset > 0;
        const a = forward ? start : 0, b = forward ? 1 : start;
        // Use integrand to calculate both range length and part
        // lengths in f(t) below.
        const ds = this._getLengthIntegrand(values);
        // Get length of total range
        const rangeLength = this._getLength(values, a, b, ds);
        const diff = Math.abs(offset) - rangeLength;
        if (Math.abs(diff) < EPSILON)
            // Matched the end:
            return forward ? b : a;
        else if (diff > EPSILON)
            // We're out of bounds.
            return undefined;
        // Use offset / rangeLength for an initial guess for t, to
        // bring us closer:
        const guess = offset / rangeLength;
        let length = 0;
        // Iteratively calculate curve range lengths, and add them up,
        // using integration precision depending on the size of the
        // range. This is much faster and also more precise than not
        // modifying start and calculating total length each time.
        const f = (t) => {
            // When start > t, the integration returns a negative value.
            length += integrate(ds, start, t, this._getIterations(start, t));
            start = t;
            return length - offset;
        };
        // Start with out initial guess for x.
        // NOTE: guess is a negative value when looking backwards.
        return findRoot(f, ds, start + guess, a, b, 32, EPSILON);
    }
    solveCubic(values, coord, val, _roots, _min, _max) {
        const v0 = values[coord];
        const v1 = values[coord + 2];
        const v2 = values[coord + 4];
        const v3 = values[coord + 6];
        let res = 0;
        // If val is outside the curve values, no solution is possible.
        if (!(v0 < val && v3 < val && v1 < val && v2 < val || v0 > val && v3 > val && v1 > val && v2 > val)) {
            const c = 3 * (v1 - v0), b = 3 * (v2 - v1) - c, a = v3 - v0 - c - b;
            res = solveCubic(a, b, c, v0 - val, _roots, _min, _max);
        }
        return res;
    }
    getIndex() {
        return this.segment1.index;
    }
    getLength() {
        return this._getLength(this.getValues(), 0, 1);
    }
    getLocationAt(offset) {
        return this.getLocationAtTime(this.getTimeAt(offset));
    }
    _getLength(values, a, b, ds) {
        if (this._isStraight(values)) {
            // Sub-divide the linear curve at a and b, so we can simply
            // calculate the Pythagorean Theorem to get the range's length.
            let c = values;
            if (b < 1) {
                c = this._subdivide(c, b)[0]; // left
                a /= b; // Scale parameter to new sub-curve.
            }
            if (a > 0) {
                c = this._subdivide(c, a)[1]; // right
            }
            // The length of straight curves can be calculated more easily.
            const dx = c[6] - c[0], // x3 - x0
            dy = c[7] - c[1]; // y3 - y0
            return Math.sqrt(dx * dx + dy * dy);
        }
        ds = ds || this._getLengthIntegrand(values);
        return integrate(this._getLengthIntegrand(values), a, b, this._getIterations(a, b));
    }
    _isStraight(values) {
        const x0 = values[0], y0 = values[1], x3 = values[6], y3 = values[7];
        const p1 = new Point(x0, y0);
        const h1 = new Point(values[2] - x0, values[3] - y0);
        const h2 = new Point(values[4] - x3, values[5] - y3);
        const p2 = new Point(x3, y3);
        if (h1.isZero() && h2.isZero()) {
            // No handles.
            return true;
        }
        else {
            const v = p2.subtract(p1);
            if (v.isZero()) {
                // Zero-length line, with some handles defined.
                return false;
            }
            else if (v.isCollinear(h1) && v.isCollinear(h2)) {
                // Collinear handles: In addition to v.isCollinear(h) checks, we
                // need to measure the distance to the line, in order to be able
                // to use the same epsilon as in Curve#getTimeOf(), see #1066.
                const line = new Line(p1, p2);
                if (line.getDistance(p1.add(h1)) < GEOMETRIC_EPSILON &&
                    line.getDistance(p2.add(h2)) < GEOMETRIC_EPSILON) {
                    // Project handles onto line to see if they are in range:
                    const div = v.dot(v), s1 = v.dot(h1) / div, s2 = v.dot(h2) / div;
                    return s1 >= 0 && s1 <= 1 && s2 <= 0 && s2 >= -1;
                }
            }
        }
        return false;
    }
    _subdivide(values, t) {
        const x0 = values[0], y0 = values[1], x1 = values[2], y1 = values[3], x2 = values[4], y2 = values[5], x3 = values[6], y3 = values[7];
        // Triangle computation, with loops unrolled.
        const u = 1 - t, 
        // Interpolate from 4 to 3 points
        x4 = u * x0 + t * x1, y4 = u * y0 + t * y1, x5 = u * x1 + t * x2, y5 = u * y1 + t * y2, x6 = u * x2 + t * x3, y6 = u * y2 + t * y3, 
        // Interpolate from 3 to 2 points
        x7 = u * x4 + t * x5, y7 = u * y4 + t * y5, x8 = u * x5 + t * x6, y8 = u * y5 + t * y6, 
        // Interpolate from 2 points to 1 point
        x9 = u * x7 + t * x8, y9 = u * y7 + t * y8;
        // We now have all the values we need to build the sub-curves:
        return [
            [x0, y0, x4, y4, x7, y7, x9, y9],
            [x9, y9, x8, y8, x6, y6, x3, y3] // right
        ];
    }
    _getLengthIntegrand(values) {
        // Calculate the coefficients of a Bezier derivative.
        const x0 = values[0], y0 = values[1], x1 = values[2], y1 = values[3], x2 = values[4], y2 = values[5], x3 = values[6], y3 = values[7], ax = 9 * (x1 - x2) + 3 * (x3 - x0), bx = 6 * (x0 + x2) - 12 * x1, cx = 3 * (x1 - x0), ay = 9 * (y1 - y2) + 3 * (y3 - y0), by = 6 * (y0 + y2) - 12 * y1, cy = 3 * (y1 - y0);
        return function (t) {
            const dx = (ax * t + bx) * t + cx;
            const dy = (ay * t + by) * t + cy;
            return Math.sqrt(dx * dx + dy * dy);
        };
    }
    // Amount of integral evaluations for the interval 0 <= a < b <= 1
    _getIterations(a, b) {
        return Math.max(2, Math.min(16, Math.ceil(Math.abs(b - a) * 32)));
    }
    ;
}
//# sourceMappingURL=curve.js.map